import { useQuery } from "@tanstack/react-query";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageDetail, PackageStatus } from "@/services/bedrock/dtos/DesignPackage";
import { DesignPackageTrace, TraceJobEvent } from "@/services/bedrock/dtos/DesignPackageTrace";
import { designKeys } from "@/services/bedrock/queryKeys";
import { isItemInProgress } from "../slideState";

export const TRACE_POLL_INTERVAL_MS = 5_000;
const RENDER_GROUP_LIMIT = 50;
const IN_PROGRESS_STATUSES: PackageStatus[] = ["queued", "generating_images", "generating_html", "rendering_previews"];

/** A create or refine is still running, so the trace is still changing. */
export function isTraceLive(pkg: Pick<DesignPackageDetail, "status" | "items" | "inFlight"> | undefined): boolean {
  if (!pkg) return false;
  return IN_PROGRESS_STATUSES.includes(pkg.status) || pkg.items.some(isItemInProgress) || pkg.inFlight != null;
}

const PENDING_JOB_STATUSES: TraceJobEvent["status"][] = ["running", "ready", "scheduled", "blocked"];

/**
 * Work the package status doesn't show: theme sample renders, the re-render after Keep, and
 * background tries all run on a completed package with nothing in flight.
 */
export function traceHasPendingWork(trace: DesignPackageTrace | undefined): boolean {
  if (!trace) return false;
  return trace.events.some(
    event =>
      (event.type === "render_group" && (event.status === "pending" || event.status === "processing")) ||
      (event.type === "job" && PENDING_JOB_STATUSES.includes(event.status))
  );
}

/**
 * The admin-only trace of a package's jobs, renders and activity. Polls while the package or the
 * last trace shows work running, so a stuck job keeps polling while the sheet is open;
 * the channel hook invalidates it once more on "updated" so the final state lands. React Query
 * pauses the interval while the tab is hidden.
 */
export function useDesignPackageTrace(pkg: DesignPackageDetail | undefined, opts: { enabled: boolean }) {
  const packageId = pkg?.id ?? null;
  return useQuery({
    queryKey: designKeys.trace(packageId ?? 0),
    queryFn: () => DesignPackageServices.trace(packageId as number, { renderGroupLimit: RENDER_GROUP_LIMIT }),
    enabled: opts.enabled && packageId != null,
    refetchInterval: query =>
      isTraceLive(pkg) || traceHasPendingWork(query.state.data) ? TRACE_POLL_INTERVAL_MS : false,
    retry: false,
  });
}
